import { Injectable, signal, computed } from '@angular/core';
import { User } from '../interfaces/user/IUser';
import { StorageKey } from '../enums/storageKey';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map } from 'rxjs';
import { UserUpdateRequest } from '../interfaces/user/IUserUpdateRequest';
import { UserUpdatePasswordRequest } from '../interfaces/user/IUserUpdatePasswordRequest';
import { EducatorDocument } from '../interfaces/user/IEducator';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly _user = signal<User | null>(this.getUserFromLocalStorage());
  readonly user = computed(() => this._user());

  constructor(private http: HttpClient) {
    const local = localStorage.getItem(StorageKey.CurrentUser);
    if (local) {
      this._user.set(JSON.parse(local));
    }
  }

  fetchMe() {
    return this.http.get<User>(`${environment.apiUrl}/user`).pipe(
      map(result => {
        localStorage.setItem(StorageKey.CurrentUser, JSON.stringify(result));
        this._user.set(result);
        return result;
      })
    );
  }

  update(data: UserUpdateRequest) {
    return this.http.patch<User>(`${environment.apiUrl}/user/account`, data).pipe(
      map(result => {
        localStorage.setItem(StorageKey.CurrentUser, JSON.stringify(result));
        this._user.set(result);
        return result;
      })
    );
  }

  updatePassword(data: UserUpdatePasswordRequest) {
    return this.http.patch(`${environment.apiUrl}/user/update-password`, data);
  }

  get me(): User | null {
    return this._user();
  }

  getFormattedName(user: User | undefined | null, fallbackEmail: boolean = true) {
    if (!user || (!user.name.callName && !user.name.surname)) {
      if (fallbackEmail) {
        return user?.firstFullName || user?.emailAddress || 'Onbekend';
      }

      return user?.firstFullName || 'Onbekend';
    }

    const nameParts = [user?.name.callName, user?.name.infix, user?.name.surname].filter(
      part => !!part && part.trim() !== ''
    );

    const formattedName = nameParts.join(' ');

    if (!formattedName) return 'Onbekend';

    return formattedName;
  }

  private getUserFromLocalStorage(): User | null {
    const raw = localStorage.getItem(StorageKey.CurrentUser);
    return raw ? (JSON.parse(raw) as User) : null;
  }

  getUserById(id: string) {
    return this.http.get<User>(`${environment.apiUrl}/user/${id}`);
  }

  updateUser(id: string, data: UserUpdateRequest) {
    return this.http.patch<User>(`${environment.apiUrl}/user/${id}`, data);
  }

  fetchEducatorDocuments(educatorId: string) {
    return this.http.get<{ documents: EducatorDocument[] }>(`${environment.apiUrl}/educator/${educatorId}/documents`);
  }
}
